/* Knurlhouse moving ridges: on a spinner-ring photo marked data-ridges, the spinning band travels slowly
   round the ring while the rims, background and studio lighting stay still.
   The photo itself is never replaced. After the page has loaded, a WebGL canvas over the band draws the
   photo's own band moving along the ring's fitted curve; the first frame matches the photo and the
   loop has no seam. Two kinds of band (the geometry file's "mode"):
   - links (The Knurl, scripts/ridges/build.py): the real links move, blending into the next real link
     once per cycle;
   - grain (The Glide, scripts/ridges/build_grain.py): a brushed band has no links, so only its fine
     grain moves, over its own shading, which stays put.
   See docs/design/turntable-build.md, "Moving ridges".
   It runs only while that photo is showing, on screen, in a visible tab, at 30 fps. The Pause spin
   button stops it; a pause is remembered (localStorage). Reduced motion starts paused. Anything
   missing (WebGL2, a different photo, Save-Data) leaves the photo still and the button hidden. */
(() => {
  const FPS = 30;
  const KEY = 'kh-spin'; // 'paused' | 'playing' once a visitor has pressed the button
  const remembered = () => { try { return localStorage.getItem(KEY); } catch { return null; } };
  const remember = (v) => { try { localStorage.setItem(KEY, v); } catch { /* private mode: this page only */ } };

  const VS = `#version 300 es
in vec2 p; out vec2 v;
void main() { v = p * .5 + .5; gl_Position = vec4(p, 0., 1.); }`;
  // Each fragment: image pixel -> across-band u and arc position s -> two real links (one pitch apart)
  // shifted by the phase, cross-faded, relit from their source position to this one.
  const FS = `#version 300 es
precision highp float;
in vec2 v; out vec4 o;
uniform sampler2D band, light;
uniform vec4 box;
uniform float cy, rad, pitch, s0, stepPx, rows, lstep, lrows, pad, yTop, yBot, phase, feather;
uniform float edgeL[5], edgeR[5], gaps[9], corr[9];
uniform float crown[32], crownX0, crownDX, crownOn;
uniform float coverOn, coverX0, coverX1, lineA, lineB;
uniform vec3 cov0, covX, covY;
uniform float lodBias;
float poly(float c[5], float y) { float t = (y - 670.) / 400.; return (((c[0]*t + c[1])*t + c[2])*t + c[3])*t + c[4]; }
float arc(float y) {
  float c = corr[0];
  for (int i = 0; i < 8; i++) if (y > gaps[i]) c = mix(corr[i], corr[i+1], clamp((y - gaps[i]) / (gaps[i+1] - gaps[i]), 0., 1.));
  return rad * asin(clamp((y - cy) / rad, -1., 1.)) + c;
}
float lightV(float row) { return ((row - .5) / lstep + .5) / lrows; }
void main() {
  vec2 ip = vec2(mix(box.x, box.z, v.x), mix(box.w, box.y, v.y));
  float l = poly(edgeL, ip.y) - pad, r = poly(edgeR, ip.y) + pad;
  float a = clamp(min(ip.x - l, r - ip.x) / feather, 0., 1.) * clamp(min(ip.y - yTop, yBot - ip.y) / 3., 0., 1.);
  if (crownOn > 0.) {   // the band's own top outline from the photo, so the links move right up to it
    float f = clamp((ip.x - crownX0) / crownDX, 0., 31.); int i = int(f);
    a *= clamp((ip.y - mix(crown[i], crown[min(i + 1, 31)], f - float(i))) / feather + .5, 0., 1.);
  }
  // Optional flat top (Knurl Silver): the links stop at the rims' top line, and above it the backdrop
  // covers the photo's link crowns, so nothing still pokes above the rims
  float ac = 0.; vec3 bg = vec3(0.);
  if (coverOn > 0.) {
    float d = lineA + lineB * ip.x - ip.y;
    if (crownOn > 0.) {   // the cover follows the band's top outline (a gentle arch above the rims)
      float f = clamp((ip.x - crownX0) / crownDX, 0., 31.); int i = int(f);
      d = mix(crown[i], crown[min(i + 1, 31)], f - float(i)) - ip.y;
    }
    a *= clamp(-d / feather + .5, 0., 1.);
    ac = clamp(d / feather + .5, 0., 1.) * clamp(min(ip.x - coverX0, coverX1 - ip.x) / 4., 0., 1.);
    bg = (cov0 + covX * ip.x + covY * ip.y) / 255.;
  }
  if (a <= 0. && ac <= 0.) { o = vec4(0.); return; }
  if (a <= 0.) { o = vec4(bg * ac, ac); return; }
  float u = (ip.x - l) / (r - l), s = arc(ip.y), w = phase / pitch;
  vec3 here = texture(light, vec2(u, lightV((s - s0) / stepPx))).rgb;
  vec3 acc = vec3(0.); float wsum = 0.;
  for (int k = 0; k < 2; k++) {
    float row = (s - phase + float(k) * pitch - s0) / stepPx;
    float wt = (k == 0 ? 1. - w : w) * clamp(min(row - .5, rows - .5 - row) / 8., 0., 1.);
    vec3 rel = clamp(here / max(texture(light, vec2(u, lightV(row))).rgb, vec3(1. / 255.)), .6, 1.6);
    acc += wt * texture(band, vec2(u, row / rows), lodBias).rgb * rel;
    wsum += wt;
  }
  if (wsum < 1e-3) { o = vec4(bg * ac, ac); return; }
  o = vec4(bg * ac + acc / wsum * a * (1. - ac), ac + a * (1. - ac));
}`;

  // Brushed band: fixed light times grain sliding round the ring (grain stored x128, periodic).
  const FS_GRAIN = `#version 300 es
precision highp float;
in vec2 v; out vec4 o;
uniform sampler2D band, light;
uniform vec4 box;
uniform float cy, rad, s0, s1, stepPx, period, lstep, lrows, pad, phase, feather;
uniform float cl[5], cr[5];
float edge(float c[5], float y) { float t = clamp((y - cy) / rad, -1., 1.), k = sqrt(1. - t*t); return c[0] + c[1]*(y - cy) + c[2]*k + c[3]*t*k + c[4]*t*t; }
void main() {
  vec2 ip = vec2(mix(box.x, box.z, v.x), mix(box.w, box.y, v.y));
  float l = edge(cl, ip.y) - pad, r = edge(cr, ip.y) + pad;
  float s = rad * asin(clamp((ip.y - cy) / rad, -1., 1.));
  float a = clamp(min(ip.x - l, r - ip.x) / feather, 0., 1.) * clamp(min(s - s0, s1 - s) / 12., 0., 1.);
  if (a <= 0.) { o = vec4(0.); return; }
  float u = (ip.x - l) / (r - l);
  vec3 lt = texture(light, vec2(u, (((s - s0) / stepPx - .5) / lstep + .5) / lrows)).rgb;
  vec3 gr = texture(band, vec2(u, mod(s - s0 - phase, period) / period)).rgb * (255. / 128.);
  o = vec4(clamp(lt * gr, 0., 1.) * a, a);
}`;

  const loadImg = (src) => new Promise((res, rej) => {
    const i = new Image(); i.crossOrigin = 'anonymous';
    i.onload = () => res(i); i.onerror = () => rej(new Error('image ' + src)); i.src = src;
  });

  async function setup(photo) {
    const stage = photo.closest('[data-stage]');
    const img = photo.querySelector('img');
    const canvas = photo.querySelector('[data-ridges-canvas]');
    const button = stage?.querySelector('[data-ridges-toggle]');
    if (!stage || !img || !canvas || !button) return;
    const g = await fetch(photo.dataset.ridgesGeom).then((r) => r.json());
    if (!img.complete) await new Promise((r) => img.addEventListener('load', r, { once: true }));
    if (g.mode === 'video') {
      if (Math.abs(img.naturalWidth / img.naturalHeight - g.w / g.h) > 0.005) return;
      return setupVideo(photo, stage, img, canvas, button, g);
    }
    const [bandImg, lightImg] = await Promise.all([loadImg(photo.dataset.ridgesBand), loadImg(photo.dataset.ridgesLight)]);
    // Shopify serves smaller widths of the same photo; anything with a different shape is a different photo.
    if (Math.abs(img.naturalWidth / img.naturalHeight - g.w / g.h) > 0.005) return;

    const gl = canvas.getContext('webgl2', { premultipliedAlpha: true, antialias: false });
    if (!gl) return;
    const shader = (type, src) => {
      const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
      return s;
    };
    const prog = gl.createProgram();
    gl.attachShader(prog, shader(gl.VERTEX_SHADER, VS)); const grain = g.mode === 'grain';
    gl.attachShader(prog, shader(gl.FRAGMENT_SHADER, grain ? FS_GRAIN : FS));
    gl.linkProgram(prog); gl.useProgram(prog);
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const at = gl.getAttribLocation(prog, 'p');
    gl.enableVertexAttribArray(at); gl.vertexAttribPointer(at, 2, gl.FLOAT, false, 0, 0);
    const texture = (unit, image, mipmaps) => {
      gl.activeTexture(gl.TEXTURE0 + unit); gl.bindTexture(gl.TEXTURE_2D, gl.createTexture());
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB8, gl.RGB, gl.UNSIGNED_BYTE, image);
      if (mipmaps) gl.generateMipmap(gl.TEXTURE_2D); // keeps the links crisp, not shimmering, when shown small
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, mipmaps ? gl.LINEAR_MIPMAP_LINEAR : gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    };
    texture(0, bandImg, true); texture(1, lightImg, false);
    const U = (n) => gl.getUniformLocation(prog, n);
    gl.uniform1i(U('band'), 0); gl.uniform1i(U('light'), 1);
    const floats = grain ? ['cy', 'rad', 's0', 's1', 'pad', 'period', 'lstep'] : ['cy', 'rad', 'pitch', 's0', 'pad', 'yTop', 'yBot', 'lstep', 'rows'];
    floats.forEach((k) => gl.uniform1f(U(k), g[k]));
    gl.uniform1f(U('stepPx'), g.step); gl.uniform1f(U('lrows'), lightImg.height);
    (grain ? ['cl', 'cr'] : ['edgeL', 'edgeR', 'gaps', 'corr']).forEach((k) => gl.uniform1fv(U(k), g[k]));
    // Optional top outline (Knurl Gold): 32 rows, one every crownX[1] photo px from crownX[0]
    if (!grain && g.crown) {
      gl.uniform1fv(U('crown'), g.crown); gl.uniform1f(U('crownX0'), g.crownX[0]); gl.uniform1f(U('crownDX'), g.crownX[1]); gl.uniform1f(U('crownOn'), 1);
    }
    // Optional flat top (Knurl Silver): links end at the line y = line[0] + line[1]*x; above it, between
    // x[0] and x[1], the backdrop colour rgb[0] + rgb[1]*x + rgb[2]*y covers the photo
    const cover = !grain && g.cover;
    if (cover) {
      gl.uniform1f(U('coverOn'), 1); gl.uniform1f(U('coverX0'), cover.x[0]); gl.uniform1f(U('coverX1'), cover.x[1]);
      gl.uniform1f(U('lineA'), cover.line[0]); gl.uniform1f(U('lineB'), cover.line[1]);
      ['cov0', 'covX', 'covY'].forEach((k, i) => gl.uniform3fv(U(k), cover.rgb[i]));
    }
    // Optional sharper links (Knurl Gold): the band is stored two rows per photo pixel along the ring, so the
    // mipmap level is picked by that axis and halves the detail across the links; lodBias takes it back
    if (!grain) gl.uniform1f(U('lodBias'), g.lodBias || 0);
    const uPhase = U('phase'), uBox = U('box'), uFeather = U('feather');

    // The band's box in photo pixels, from its widest edges
    const poly = (c, y) => { const t = (y - 670) / 400; return (((c[0] * t + c[1]) * t + c[2]) * t + c[3]) * t + c[4]; };
    const edge = (c, y) => { const t = Math.max(-1, Math.min(1, (y - g.cy) / g.rad)), k = Math.sqrt(1 - t * t); return c[0] + c[1] * (y - g.cy) + c[2] * k + c[3] * t * k + c[4] * t * t; };
    const yTop = grain ? Math.floor(g.cy + g.rad * Math.sin(g.s0 / g.rad)) : g.yTop;
    const yBot = grain ? Math.ceil(g.cy + g.rad * Math.sin(g.s1 / g.rad)) : g.yBot;
    let x0 = Infinity, x1 = -Infinity;
    for (let y = yTop; y <= yBot; y++) {
      x0 = Math.min(x0, (grain ? edge(g.cl, y) : poly(g.edgeL, y)) - g.pad);
      x1 = Math.max(x1, (grain ? edge(g.cr, y) : poly(g.edgeR, y)) + g.pad);
    }
    if (cover) { x0 = Math.min(x0, cover.x[0]); x1 = Math.max(x1, cover.x[1]); }
    const box = [Math.floor(x0 - 2), Math.min(yTop, cover ? cover.y0 : yTop) - 1, Math.ceil(x1 + 2), yBot + 1];
    // Links: one pitch per "second per link" (the section setting), looping every pitch. Grain: one whole
    // loop in the geometry file's loopSeconds, whatever the link setting.
    const loop = grain ? g.period : g.pitch;
    const step = grain ? g.period / g.loopSeconds * (Number(photo.dataset.ridgesSeconds) || 2.5) : g.pitch;

    let phase = 0;
    const draw = () => { gl.uniform1f(uPhase, phase); gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4); };
    // Follow wherever the stage's CSS draws the photo (editorial framing, phone or desktop stage)
    const layout = () => {
      const pr = photo.getBoundingClientRect(), ir = img.getBoundingClientRect();
      if (!ir.height) return;
      // maxDpr (Knurl Gold) lets the band draw at the screen's full density, and at pinch-zoom too, so it
      // isn't stretched next to the photo, which the browser redraws sharp when zoomed
      const zoom = g.maxDpr ? (window.visualViewport ? window.visualViewport.scale : 1) : 1;
      const sc = ir.height / g.h, dpr = Math.min((window.devicePixelRatio || 1) * zoom, g.maxDpr || 2);
      const w = (box[2] - box[0]) * sc, h = (box[3] - box[1]) * sc;
      Object.assign(canvas.style, { left: `${ir.left - pr.left + box[0] * sc}px`, top: `${ir.top - pr.top + box[1] * sc}px`, width: `${w}px`, height: `${h}px` });
      canvas.width = Math.max(1, Math.round(w * dpr)); canvas.height = Math.max(1, Math.round(h * dpr));
      gl.viewport(0, 0, canvas.width, canvas.height);
      // Map the rounded backing size back to photo pixels so the band lines up exactly
      gl.uniform4f(uBox, box[0], box[1], box[0] + canvas.width / (sc * dpr), box[1] + canvas.height / (sc * dpr));
      gl.uniform1f(uFeather, Math.max(1.5, 1 / (sc * dpr)));
      draw();
    };

    const speed = Number(photo.dataset.ridgesSeconds) || 2.5; // seconds per link
    const reduce = matchMedia('(prefers-reduced-motion: reduce)');
    let paused = remembered() ? remembered() === 'paused' : reduce.matches;
    let visible = false, raf = 0, last = 0;
    const label = button.querySelector('[data-ridges-label]');
    const showState = () => {
      button.dataset.state = paused ? 'paused' : 'playing';
      label.textContent = paused ? button.dataset.play : button.dataset.pause;
    };
    const tick = (t) => {
      raf = requestAnimationFrame(tick);
      if (t - last < 1000 / FPS - 2) return;
      phase = (phase + Math.min(t - last, 100) / 1000 / speed * step) % loop;
      last = t;
      draw();
    };
    const sync = () => {
      const run = !paused && visible && !document.hidden && photo.classList.contains('is-active');
      if (run && !raf) { last = performance.now(); raf = requestAnimationFrame(tick); }
      if (!run && raf) { cancelAnimationFrame(raf); raf = 0; }
    };

    button.addEventListener('click', () => {
      paused = !paused;
      remember(paused ? 'paused' : 'playing');
      showState(); sync();
    });
    reduce.addEventListener('change', () => { if (!remembered()) { paused = reduce.matches; showState(); sync(); } });
    new MutationObserver(sync).observe(photo, { attributes: true, attributeFilter: ['class'] });
    new IntersectionObserver(([e]) => { visible = e.isIntersecting; sync(); }).observe(stage);
    document.addEventListener('visibilitychange', sync);
    new ResizeObserver(layout).observe(stage);
    if (g.maxDpr && window.visualViewport) {   // redraw sharper once a pinch-zoom settles
      let zt = 0;
      window.visualViewport.addEventListener('resize', () => { clearTimeout(zt); zt = setTimeout(layout, 150); });
    }
    canvas.addEventListener('webglcontextlost', () => {
      cancelAnimationFrame(raf); raf = 0; paused = true;
      canvas.hidden = true; button.hidden = true;
    });

    layout();
    showState();
    canvas.classList.add('is-on');
    button.hidden = false;
    sync();
  }

  // Pre-rendered loop (mode "video", The Facet preview): a silent looping video of the ring area, laid
  // over the photo exactly like the canvas, with the same pill, pause memory and visibility rules.
  function setupVideo(photo, stage, img, canvas, button, g) {
    const video = document.createElement('video');
    Object.assign(video, { muted: true, loop: true, playsInline: true, preload: 'auto', disablePictureInPicture: true });
    video.setAttribute('muted', ''); video.setAttribute('playsinline', ''); video.setAttribute('aria-hidden', 'true');
    video.className = 'kht-ridges'; video.src = photo.dataset.ridgesVideo;
    canvas.replaceWith(video);
    const [bx0, by0, bx1, by1] = g.box;
    const layout = () => {
      const pr = photo.getBoundingClientRect(), ir = img.getBoundingClientRect();
      if (!ir.height) return;
      const sc = ir.height / g.h;
      Object.assign(video.style, { left: `${ir.left - pr.left + bx0 * sc}px`, top: `${ir.top - pr.top + by0 * sc}px`,
        width: `${(bx1 - bx0) * sc}px`, height: `${(by1 - by0) * sc}px` });
    };
    const reduce = matchMedia('(prefers-reduced-motion: reduce)');
    let paused = remembered() ? remembered() === 'paused' : reduce.matches;
    let visible = false;
    const label = button.querySelector('[data-ridges-label]');
    const showState = () => { button.dataset.state = paused ? 'paused' : 'playing'; label.textContent = paused ? button.dataset.play : button.dataset.pause; };
    const sync = () => {
      const run = !paused && visible && !document.hidden && photo.classList.contains('is-active');
      if (run) video.play().catch(() => {}); else video.pause();
    };
    button.addEventListener('click', () => { paused = !paused; remember(paused ? 'paused' : 'playing'); showState(); sync(); });
    reduce.addEventListener('change', () => { if (!remembered()) { paused = reduce.matches; showState(); sync(); } });
    new MutationObserver(sync).observe(photo, { attributes: true, attributeFilter: ['class'] });
    new IntersectionObserver(([e]) => { visible = e.isIntersecting; sync(); }).observe(stage);
    document.addEventListener('visibilitychange', sync);
    new ResizeObserver(layout).observe(stage);
    video.addEventListener('loadeddata', () => {
      layout(); showState();
      video.classList.add('is-on'); button.hidden = false; sync();
    }, { once: true });
    video.addEventListener('error', () => { video.remove(); button.hidden = true; });
  }

  const start = () => document.querySelectorAll('[data-ridges]').forEach((photo) => {
    setup(photo).catch(() => { /* the photo simply stays still */ });
  });
  const idle = () => (window.requestIdleCallback ? requestIdleCallback(start, { timeout: 1500 }) : setTimeout(start, 200));
  // Never competes with the page itself: nothing is fetched until the page has finished loading.
  if (navigator.connection?.saveData) return;
  if (document.readyState === 'complete') idle(); else window.addEventListener('load', idle, { once: true });
})();
